import React from "react";
import ReactDOM from "react-dom";
import {
  autocomplete,
  AutocompleteComponents,
  AutocompleteState,
  getAlgoliaResults,
} from "@algolia/autocomplete-js";
import "@algolia/autocomplete-theme-classic";
import {
  InputWrapperPrefixClass,
  InputWrapperClass,
  SubmitButtonClass,
  LabelClass,
  RootClass,
  InputWrapperSuffixClass,
  PanelClass,
  SourceHeader,
  PanelLayoutClass,
  ItemLinkClass,
  ItemContentClass,
  ItemContentBodyClass,
  ItemContentTitleClass,
  ItemActionsClass,
  ItemClass,
  Source,
  ArrowClass,
  SourceHeaderViewMore,
  SourceHeaderText,
  InputClass,
} from "./search-autocomplete.styled";
import { algoliaClient, prismicTabName, helpCenterTabName } from "../config";
import { PrismicRecordType } from "../../../types/types";
import {
  prismicAlgoliaIndexName,
  helpCenterAlgoliaIndexName,
  helpCenterURLBase,
} from "../../../utils/constants";
import { navigate } from "gatsby";
import { track, trackSearchResultClicked } from "../../../utils/analytics";
import { HelpCenterHitType } from "../search-content/help-center-hits/help-center-hits";
import * as FA from "../../../styles/fontawesome";

type AutocompleteFields = {
  __autocomplete_queryID: string; // populated by Algolia Autocomplete, missing from Algolia types
  position: number; // populated by transformResponse
};

type PrismicAutocompleteType = PrismicRecordType & AutocompleteFields;
type HelpCenterAutocompleteType = HelpCenterHitType & AutocompleteFields;
type AllResultsAutocompleteType = { allTitle: string };

export function startAutocomplete(
  container: HTMLDivElement,
  panel: HTMLDivElement,
  onClickResult: (query: string, index?: number, tabName?: string) => void,
  initialQuery: string
) {
  return autocomplete<any>({
    container: container,
    // NOTE: on upgrade to React 18, this changes, see https://www.algolia.com/doc/ui-libraries/autocomplete/integrations/using-react/#with-react-18
    renderer: {
      createElement: React.createElement,
      Fragment: React.Fragment,
      render: ReactDOM.render as any,
    },
    panelContainer: panel,
    placeholder: 'Try "CNC Machining"',
    openOnFocus: false,
    detachedMediaQuery: "none",
    initialState: { query: initialQuery },
    onSubmit: ({ state }) => {
      if (state.query && state.query !== "") {
        track("Performed Search", { searchTerm: state.query });
        onClickResult(state.query);
      }
    },
    classNames: {
      inputWrapper: InputWrapperClass,
      inputWrapperPrefix: InputWrapperPrefixClass,
      submitButton: SubmitButtonClass,
      label: LabelClass,
      root: RootClass,
      inputWrapperSuffix: InputWrapperSuffixClass,
      panel: PanelClass,
      panelLayout: PanelLayoutClass,
      sourceHeader: SourceHeader,
      item: ItemClass,
      source: Source,
      input: InputClass,
    },
    getSources({ query }) {
      return [
        {
          sourceId: "all",
          getItems({ query }) {
            return [
              {
                allTitle: `View all results for ${query}`,
              },
            ];
          },
          templates: {
            header() {
              return <span>All Results</span>;
            },
            item({ item, state }) {
              return (
                <AutocompleteAllResult
                  hit={item}
                  state={state}
                  onClickResult={onClickResult}
                />
              );
            },
          },
        },
        {
          sourceId: "website",
          getItems() {
            return getAlgoliaResults<PrismicAutocompleteType>({
              searchClient: algoliaClient,
              queries: [
                {
                  indexName: prismicAlgoliaIndexName,
                  query,
                  params: {
                    hitsPerPage: 5,
                    clickAnalytics: true,
                    snippetEllipsisText: "…",
                    attributesToSnippet: ["heading"],
                  },
                },
              ],
              transformResponse({ hits: allHits }) {
                return allHits.map((sourceHits) => {
                  return sourceHits.map(
                    (hit: { position: any }, position: any) => {
                      hit.position = position;
                      return hit;
                    }
                  );
                });
              },
            });
          },
          templates: {
            header({ state }) {
              return (
                <SourceHeaderElement
                  text="Website"
                  indexQS={prismicTabName}
                  query={state.query}
                />
              );
            },
            item({ item, components, state }) {
              return (
                <PrismicAutocompleteResult
                  hit={item}
                  components={components}
                  state={state}
                  queryID={item.__autocomplete_queryID}
                />
              );
            },
          },
        },
        {
          sourceId: "help_center",
          getItems() {
            return getAlgoliaResults<HelpCenterAutocompleteType>({
              searchClient: algoliaClient,
              queries: [
                {
                  indexName: helpCenterAlgoliaIndexName,
                  query,
                  params: {
                    hitsPerPage: 5,
                    clickAnalytics: true,
                    snippetEllipsisText: "…",
                    attributesToSnippet: ["title"],
                  },
                },
              ],
              transformResponse({ hits: allHits }) {
                return allHits.map((sourceHits) => {
                  return sourceHits.map(
                    (hit: { position: any }, position: any) => {
                      hit.position = position;
                      return hit;
                    }
                  );
                });
              },
            });
          },
          templates: {
            header({ state }) {
              return (
                <SourceHeaderElement
                  text="Help Center"
                  indexQS={helpCenterTabName}
                  query={state.query}
                />
              );
            },
            item({ item, components, state }) {
              return (
                <HelpCenterAutocompleteResult
                  hit={item}
                  components={components}
                  state={state}
                  queryID={item.__autocomplete_queryID}
                />
              );
            },
          },
        },
      ];
    },
  });
}

type SourceHeaderElementProps = {
  text: string;
  query: string;
  indexQS: "All" | "Help Center";
};

function SourceHeaderElement(props: SourceHeaderElementProps) {
  const { text, indexQS, query } = props;
  return (
    <>
      <span className={SourceHeaderText}>{text}</span>
      <a
        href={`/search/?index=${indexQS}&query=${query}`}
        className={SourceHeaderViewMore}
      >
        View More
      </a>
    </>
  );
}

type PrismicAutocompleteResultProps = {
  hit: PrismicAutocompleteType;
  components: AutocompleteComponents;
  state: AutocompleteState<PrismicAutocompleteType>;
  queryID: string;
};

function PrismicAutocompleteResult({
  hit,
  components,
  state,
  queryID,
}: PrismicAutocompleteResultProps) {
  return (
    <a
      onClick={() => {
        trackSearchResultClicked(
          hit.heading,
          prismicAlgoliaIndexName,
          state.query,
          hit,
          queryID,
          0,
          "autocomplete",
          hit.position
        );
        navigate(hit.relativeUrl);
      }}
      className={ItemLinkClass}
    >
      <div className={ItemContentClass}>
        <div className={ItemContentBodyClass}>
          <div className={ItemContentTitleClass}>
            <components.Snippet hit={hit} attribute={"heading"} />
          </div>
        </div>
      </div>
      <div className={ItemActionsClass}>
        <i css={FA.ArrowRightRegular} className={ArrowClass} />
      </div>
    </a>
  );
}

type HelpCenterAutocompleteResultProps = {
  hit: HelpCenterAutocompleteType;
  components: AutocompleteComponents;
  state: AutocompleteState<HelpCenterAutocompleteType>;
  queryID: string;
};

function HelpCenterAutocompleteResult({
  hit,
  components,
  state,
  queryID,
}: HelpCenterAutocompleteResultProps) {
  return (
    <a
      href={`${helpCenterURLBase}${hit.id}`}
      onClick={() => {
        trackSearchResultClicked(
          hit.title,
          helpCenterAlgoliaIndexName,
          state.query,
          hit,
          queryID,
          0,
          "autocomplete",
          hit.position
        );
      }}
      className={ItemLinkClass}
    >
      <div className={ItemContentClass}>
        <div className={ItemContentBodyClass}>
          <div className={ItemContentTitleClass}>
            <components.Snippet hit={hit} attribute={"title"} />
          </div>
        </div>
      </div>
      <div className={ItemActionsClass}>
        <i css={FA.ArrowRightRegular} className={ArrowClass} />
      </div>
    </a>
  );
}

type AutocompleteAllResultProps = {
  hit: AllResultsAutocompleteType;
  state: AutocompleteState<AllResultsAutocompleteType>;
  onClickResult: (query: string) => void;
};

function AutocompleteAllResult({
  hit,
  state,
  onClickResult,
}: AutocompleteAllResultProps) {
  const lastWordIndex = hit.allTitle.lastIndexOf(" ");
  return (
    <a
      onClick={() => {
        onClickResult(state.query);
      }}
      className={ItemLinkClass}
    >
      <div className={ItemContentClass}>
        <div className={ItemContentBodyClass}>
          <div className={ItemContentTitleClass}>
            {hit.allTitle.substring(0, lastWordIndex)}
            <b>{hit.allTitle.substring(lastWordIndex)}</b>
          </div>
        </div>
      </div>
      <div className={ItemActionsClass}>
        <i css={FA.ArrowRightRegular} className={ArrowClass} />
      </div>
    </a>
  );
}
